import type { StipulationView } from "./api/types";

export type BankApplicantType = "driver" | "secondSigner";
export type BankAccountType = "checking" | "savings";

interface BankStipCodeInfo {
  applicant: BankApplicantType;
  accountType: BankAccountType;
}

// Canonical stipCodes that back the Verify button. `accountType` isn't
// exposed on `StipulationListItem`, so it's keyed off these codes; `applicant`
// here is only a fallback for a response that omits the item's own
// `applicantType` — see `buildBankAccountOwnerOptions`, which prefers that
// field so this table can't silently disagree with it.
export const BANK_STIP_CODES: Record<string, BankStipCodeInfo> = {
  DRV_PROF_CHK: { applicant: "driver", accountType: "checking" },
  DRV_PROF_SVG: { applicant: "driver", accountType: "savings" },
  SIG2_PROF_CHK: { applicant: "secondSigner", accountType: "checking" },
  SIG2_PROF_SVG: { applicant: "secondSigner", accountType: "savings" },
};

export interface BankAccountOwnerOption {
  value: string;
  label: string;
  applicantPk: number;
  stipCode: string;
  accountType: BankAccountType;
}

interface ResidenceName {
  firstName?: string;
  lastName?: string;
}

/**
 * Joins a residence's first and last name.
 * @param {ResidenceName} [residence] - The residence name parts.
 * @returns {string | undefined} The full name, or undefined when both parts are blank.
 */
export function fullNameFromResidence(residence?: ResidenceName): string | undefined {
  const name = [residence?.firstName, residence?.lastName].filter(Boolean).join(" ");
  return name || undefined;
}

interface BuildBankAccountOwnerOptionsParameters {
  items: StipulationView[];
  accountTypeLabels: Record<BankAccountType, string>;
  applicantLabels: Record<BankApplicantType, string>;
  applicantFullNames: Partial<Record<BankApplicantType, string>>;
}

/**
 * Builds "Checking - Driver (John Smith)"-style options for bank stips with isBankVerificationAllowed.
 * @param {BuildBankAccountOwnerOptionsParameters} root0 - Build inputs.
 * @param {StipulationView[]} root0.items - Stips to derive options from.
 * @param {Record<BankAccountType, string>} root0.accountTypeLabels - Display label per account type.
 * @param {Record<BankApplicantType, string>} root0.applicantLabels - Display label per applicant type.
 * @param {Partial<Record<BankApplicantType, string>>} root0.applicantFullNames - Full name per applicant type.
 * @returns {BankAccountOwnerOption[]} The de-duplicated owner options.
 */
export function buildBankAccountOwnerOptions({
  items,
  accountTypeLabels,
  applicantLabels,
  applicantFullNames,
}: BuildBankAccountOwnerOptionsParameters): BankAccountOwnerOption[] {
  const seen = new Set<string>();
  const options: BankAccountOwnerOption[] = [];

  for (const item of items) {
    const info = BANK_STIP_CODES[item.stipCode];
    if (!item.isBankVerificationAllowed || !info || item.applicantPk === undefined) {
      continue;
    }
    // The item's own `applicantType` is the source of truth (same field the
    // Stipulations panel scopes by) — the table's `applicant` is only a
    // fallback for a response that omits it.
    const applicant = item.applicantType ?? info.applicant;

    const value = `${applicant}-${info.accountType}`;
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);

    const fullName = applicantFullNames[applicant];
    const applicantLabel = applicantLabels[applicant];
    const accountTypeLabel = accountTypeLabels[info.accountType];
    const label = fullName
      ? `${accountTypeLabel} - ${applicantLabel} (${fullName})`
      : `${accountTypeLabel} - ${applicantLabel}`;

    options.push({
      value,
      label,
      applicantPk: item.applicantPk,
      stipCode: item.stipCode,
      accountType: info.accountType,
    });
  }

  return options;
}
